import type {
  WorkflowDefinitionResponse,
  WorkflowTemplateResponse,
} from '@erp/rpc';
import { SocketTypeSchema, StepCategorySchema } from '@erp/workflows';

type StepDefinition = WorkflowDefinitionResponse['steps'][0];
type EdgeDefinition = WorkflowDefinitionResponse['edges'][0];
type SocketType = StepDefinition['sockets']['inputs'][0]['type'];
type StepCategory = StepDefinition['category'];

function toStepCategory(category: string): StepCategory {
  const parsed = StepCategorySchema.safeParse(category);
  return parsed.success ? parsed.data : 'Utility';
}

function toSocketType(socketType: string): SocketType {
  const parsed = SocketTypeSchema.safeParse(socketType);
  return parsed.success ? parsed.data : 'any';
}

export function templateToEditorDefinition(
  template: WorkflowTemplateResponse,
): WorkflowDefinitionResponse {
  const now = new Date().toISOString();

  const stepIdMap = new Map<number, string>(); // index -> UUID
  template.steps.forEach((_, index) => {
    stepIdMap.set(index, crypto.randomUUID());
  });

  const steps: StepDefinition[] = template.steps.map((step, index) => {
    const stepId = stepIdMap.get(index);
    if (!stepId) {
      throw new Error(`Missing UUID for template step index: ${index}`);
    }

    return {
      id: stepId,
      type: step.stepType,
      name: step.name,
      category: toStepCategory(step.category),
      config: step.config,
      sockets: {
        inputs: step.sockets.inputs.map((socket) => ({
          id: socket.id,
          name: socket.name,
          type: toSocketType(socket.type),
          required: socket.required,
          description: socket.description,
        })),
        outputs: step.sockets.outputs.map((socket) => ({
          id: socket.id,
          name: socket.name,
          type: toSocketType(socket.type),
          required: socket.required,
          description: socket.description,
        })),
      },
      position: step.position,
    };
  });

  const edges: EdgeDefinition[] = template.edges.map((edge) => {
    const sourceStepId = stepIdMap.get(edge.sourceStepIndex);
    const targetStepId = stepIdMap.get(edge.targetStepIndex);

    if (!sourceStepId) {
      throw new Error(
        `Missing source step UUID for template step index: ${edge.sourceStepIndex}`,
      );
    }
    if (!targetStepId) {
      throw new Error(
        `Missing target step UUID for template step index: ${edge.targetStepIndex}`,
      );
    }

    return {
      id: crypto.randomUUID(),
      sourceStepId,
      sourceSocketId: edge.sourceSocketId,
      targetStepId,
      targetSocketId: edge.targetSocketId,
    };
  });

  return {
    id: 'new',
    name: template.name,
    description: template.description,
    tags: template.tags,
    version: 1,
    isActive: false,
    organizationId: '',
    createdBy: '',
    createdAt: now,
    updatedAt: now,
    steps,
    edges,
  };
}
